import React from 'react';
import { Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  ChartOptions
} from 'chart.js';
import { FiAlertCircle } from 'react-icons/fi';
import { useExecutiveCreditUsageRate } from '../../hooks/useExecutiveQueries';
import { useTheme } from '../../contexts/ThemeContext';
import { getChartColors } from '../../utils/themeHelpers';
import { LoadingSpinner, ChartInfoButton } from '../ui';
import { CHART_GUIDES } from '../../constants/chartGuides';
import useChartGuide from '../../hooks/useChartGuide';

ChartJS.register(ArcElement, Tooltip, Legend);
const AlertCircleIcon = FiAlertCircle as React.ElementType;

interface AverageCreditUsageRateProps {
  branchId?: number;
  timeRange?: any;
  onError?: (error: Error) => void;
  showLoading?: boolean;
  compact?: boolean;
}

const AverageCreditUsageRate: React.FC<AverageCreditUsageRateProps> = ({
  branchId,
  timeRange,
  onError,
  showLoading = true,
  compact = false
}) => {
  const { currentTheme } = useTheme();
  const chartColors = getChartColors(currentTheme);
  const { openGuide } = useChartGuide();
  
  // Fetch credit usage rate data with timeRange
  const { data, isLoading, error } = useExecutiveCreditUsageRate(branchId, timeRange?.timeRange);
  
  const usageRate = data?.averageUsageRate || 0;
  const kpis = data?.kpis || {};
  
  // Chart data configuration
  const chartData = {
    labels: ['مصرف شده', 'باقیمانده'],
    datasets: [
      {
        data: [usageRate, 100 - usageRate],
        backgroundColor: [chartColors[0], 'var(--border-primary)'],
        borderWidth: 0,
        cutout: '75%',
      },
    ],
  };

  // Chart options configuration
  const chartOptions: ChartOptions<'doughnut'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
    },
    rotation: -90,
    circumference: 360,
  };
  
  // Handle loading state
  if (showLoading && isLoading) {
    return (
      <div className="card-base card-padding">
        <div className="flex items-center justify-center min-h-[280px]">
          <LoadingSpinner
            variant="minimal"
            size="lg"
            message="در حال بارگذاری نرخ مصرف اعتبار…"
          />
        </div>
      </div>
    );
  }
  
  // Handle error
  if (error) {
    if (onError && error instanceof Error) {
      onError(error);
    }
    return null;
  }
  
  return (
    <div className="card-base card-padding">
      <ChartInfoButton 
        onClick={() => openGuide(CHART_GUIDES.creditUsageRate)}
        className="mb-4"
      />
      <h3 className="title-card">متوسط نرخ مصرف اعتبار در دوره زمانی</h3>
      
      <div className={`flex ${compact ? 'flex-col' : 'flex-col md:flex-row'} items-center`}>
        <div className={`w-full ${compact ? 'md:w-full' : 'md:w-1/2'}`}>
          <div className="relative w-full h-[180px]">
            <Doughnut data={chartData} options={chartOptions} />
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <div className="text-2xl font-bold content-primary">
                {usageRate.toFixed(1)}%
              </div>
              <div className="text-sm content-secondary">
                متوسط نرخ مصرف
              </div>
            </div>
          </div>
        </div>
        
        <div className={`w-full ${compact ? 'md:w-full mt-4' : 'md:w-1/2 mt-4 md:mt-0'}`}>
          <div className="flex items-start gap-3 p-4 rounded-lg bg-status-error-bg border border-status-error">
            <AlertCircleIcon size={16} className="text-status-error flex-shrink-0 mt-1" />
            <div className="flex-1">
              <p className="text-sm text-text-secondary">
                تنها {usageRate.toFixed(1)}% از اعتبارات تولید شده مصرف می‌شوند که نشان دهنده کارایی پایین سیستم است.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AverageCreditUsageRate;
